import { Button, Container, Form } from 'react-bootstrap';
import { Row, Col } from "react-bootstrap";
import { useState, useCallback, useMemo } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import DroppableColumn from './DroppableColumn';
import DraggableCard from './DraggableCard';
import { useEffect } from 'react';
import { AlertStatusEnum, ExportCSVData, AlertCard, UpdateStatusAlert } from '../types';
import { changeDestinationAlert } from '../api/updateAlertStatus';
import Search from '../../../components/Search.tsx'
import { useToken } from '../../../utils/auth';
import { useTranslation } from "react-i18next";
import { useAlert } from "../../../hooks/useAlert";
import AlertMessage from "../../../components/AlertMessage";
import { useAlertModal } from '../hooks/useAlertModal.ts';
import ModalGlobal from '../../../components/AppModal/ModalGlobal.tsx';
import { saveAlertsCsv } from '../api/saveAlertsCsv.ts';
import { SelectedFilter } from '../../../components/Filters/types/index.ts';
import { FilterDto, FilterType } from '../../../types/FilterDto.ts';
import { useUsersSelect } from '../../../hooks/getUsersByClient.tsx';
import FiltersContainer from '../../../components/Filters/components/FiltersContainer.tsx';
import { InputDatePicker } from '../../../components/InputDatePicker.tsx';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../redux/store.ts';
import { setFilterByCurrentUser } from '../../../redux/filtersSlice.ts';

const INITIAL_COLUMN_DATA = [
    { id: "1", name: "dashboardAlertsColumn1", items: [] as AlertCard[] },
    { id: "2", name: "dashboardAlertsColumn2", items: [] as AlertCard[] },
    { id: "3", name: "dashboardAlertsColumn3", items: [] as AlertCard[] },
    { id: "4", name: "dashboardAlertsColumn4", items: [] as AlertCard[] },
];

const ORIGIN_MAP: Record<string, number> = {
    "EnquestaAPacient": 0,
    "Demandes": 1,
    "XarxesSocials": 2,
    "EnquestesAprofessionals": 3
};

interface AlertsDADProps {
    urgenciesStates: AlertCard[];
    onSurveyClick: (surveyId: number) => void;
    onDataUpdate?: (updatedData: AlertCard[]) => void;
    onClaimClick: (claimId: number) => void;
}

function buildColumns(data: AlertCard[]) {
    return INITIAL_COLUMN_DATA.map((column) => ({
        ...column,
        items: data
            .filter((urgency) => urgency.status.toString() === column.id)
            .sort((a, b) => a.order - b.order),
    }));
}

export default function AlertsDAD({ urgenciesStates, onSurveyClick, onClaimClick }: AlertsDADProps) {
    const { t } = useTranslation(['common', 'alert']);

    const alertSelectedFilters = useSelector((state: RootState) => state.filters.alerts.selectedFilters);
    const dispatch = useDispatch();
    const { filterByCurrentUser } = useSelector((state: RootState) => state.filters.alerts);

    const currentUserId = useToken()?.decoded.userId;
    const { data: users } = useUsersSelect({});

    const [searchText, setSearchText] = useState('');
    const [description, setDescription] = useState('');
    const [filters, setFilters] = useState<FilterDto[]>([]);
    const [appliedFilters, setAppliedFilters] = useState<SelectedFilter[]>([]);
    const [isFilterVisible, setIsFilterVisible] = useState(false);

    const [originalData, setOriginalData] = useState<AlertCard[]>(urgenciesStates);

    useEffect(() => {
        setOriginalData(urgenciesStates);
    }, [urgenciesStates]);

    const filteredData = useMemo(() => {
        const originFilter = appliedFilters.find(x => x.name === "Origin");
        const originIds = (originFilter?.values ?? [])
            .map(origin => ORIGIN_MAP[origin])
            .filter(id => id !== undefined);

        const userFilter = appliedFilters.find(f => f.name === "UserRelatedAlert");
        const selectedUsers = userFilter?.values?.map(v => Number(v)) ?? [];

        return originalData.filter((urgency) => {
            const matchesOrigin =
                originIds.length === 0 || originIds.includes(urgency.referenceTable);

            const matchesUser =
                selectedUsers.length === 0
                || (selectedUsers.includes(-1) && (urgency.userId == null))
                || selectedUsers.includes(urgency.userId);

            const matchesDescription =
                !description
                || urgency.comment?.toLowerCase().includes(description.toLowerCase())
                || urgency.referenceId?.toString().includes(description.toLowerCase());

            const matchesCurrentUser =
                !filterByCurrentUser || urgency.userId === Number(currentUserId);

            return matchesOrigin && matchesUser && matchesDescription && matchesCurrentUser;
        });
    }, [originalData, appliedFilters, description, filterByCurrentUser, currentUserId]);

    const [columnsData, setColumnsData] = useState(() => buildColumns(urgenciesStates));

    useEffect(() => {
        setColumnsData(buildColumns(filteredData));
    }, [filteredData]);

    useEffect(() => {
        if (!users || users.length === 0) return;
        setFilters([
            {
                name: "UserRelatedAlert",
                description: t("alert:filters.UserRelatedAlert"),
                type: FilterType.MULTIPLE,
                values: [
                    { key: -1, value: "alert:filters.Unassigned" },
                    ...users.map(user => ({ key: user.id, value: user.name }))
                ]
            },
            {
                name: "Origin",
                description: t("alert:filters.OrigenAlerta"),
                type: FilterType.MULTIPLE,
                values: [
                    { key: "EnquestaAPacient", value: t("alert:filters.EnquestaAPacient") },
                    { key: "Demandes", value: t("alert:filters.Queixes") },
                    { key: "XarxesSocials", value: t("alert:filters.XarxesSocials") },
                    { key: "EnquestesAProfessionals", value: t("alert:filters.EnquestesAProfessionals") }
                ]
            }
        ]);
        setAppliedFilters(alertSelectedFilters);
    }, [users]);

    const handleSearchChange = useCallback((value: string) => setSearchText(value), []);
    const handleSearch = useCallback(() => setDescription(searchText), [searchText]);
    const handleClear = useCallback(() => { setSearchText(''); setDescription(''); }, []);
    const handleFiltersChange = useCallback((f: SelectedFilter[]) => setAppliedFilters(f), []);
    const handleFilterToggle = useCallback(() => setIsFilterVisible(v => !v), []);

    const handleToggle = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setFilterByCurrentUser({ table: 'alerts', filterByCurrentUser: e.target.checked }));
        // columnsData will rebuild automatically via the filteredData memo + useEffect
    }, [dispatch]);

    const handleDragDrop = useCallback((result: DropResult) => {
        const { draggableId, source, destination } = result;
        if (!destination) return;
        if (source.droppableId === destination.droppableId && source.index === destination.index) return;

        setColumnsData((prevColumns) => {
            const sourceColumn = prevColumns.find(c => c.id === source.droppableId);
            const destColumn = prevColumns.find(c => c.id === destination.droppableId);
            if (!sourceColumn || !destColumn) return prevColumns;

            const isSameColumn = sourceColumn.id === destColumn.id;

            // Work on copies
            const sourceItems = [...sourceColumn.items];
            const destItems = isSameColumn ? sourceItems : [...destColumn.items];

            // Remove from source and update status
            const [movedItem] = sourceItems.splice(source.index, 1);
            const updatedMovedItem = { ...movedItem, status: parseInt(destColumn.id) };

            // Insert at destination index
            destItems.splice(destination.index, 0, updatedMovedItem);

            // ✅ Recalculate `order` for every item in each affected column
            //    so that React sees real data changes and re-renders correctly
            const reorder = (items: AlertCard[]) =>
                items.map((item, idx) => ({ ...item, order: idx }));

            const reorderedSource = reorder(sourceItems);
            const reorderedDest = isSameColumn ? reorderedSource : reorder(destItems);

            // Persist to API — revert on failure
            const body: UpdateStatusAlert = {
                idAlert: parseInt(draggableId),
                droppableId: destination.droppableId,
                index: destination.index
            };

            changeDestinationAlert(body)
                .then(() => {
                    // Sync originalData with new status AND new order values
                    setOriginalData(prev => {
                        const updatedMap = new Map<number, Partial<AlertCard>>();

                        reorderedSource.forEach(item => updatedMap.set(item.id, { order: item.order }));
                        if (!isSameColumn) {
                            reorderedDest.forEach(item =>
                                updatedMap.set(item.id, { status: item.status, order: item.order })
                            );
                        }

                        return prev.map(item => {
                            const patch = updatedMap.get(item.id);
                            return patch ? { ...item, ...patch } : item;
                        });
                    });
                })
                .catch((error) => {
                    console.error('Error updating alert state:', error);
                    // Revert optimistic update by re-triggering filteredData → columns rebuild
                    setOriginalData(prev => [...prev]);
                });

            return prevColumns.map(column => {
                if (column.id === sourceColumn.id && column.id === destColumn.id) {
                    return { ...column, items: reorderedSource };
                }
                if (column.id === sourceColumn.id) return { ...column, items: reorderedSource };
                if (column.id === destColumn.id) return { ...column, items: reorderedDest };
                return column;
            });
        });
    }, []);

    const { openAlertModal, showModal, closeModal, error, endDate, setEndDate, startDate, setStartDate } = useAlertModal();
    const { alert, hideAlert, showAlert, isVisible } = useAlert({ fromLocation: false });

    const [selectedUserModal, setSelectedUserModal] = useState<string>('');
    const [selectedOptions, setSelectedOptions] = useState<number[]>([]);

    const onEndDateChange = (date: Date | null) => setEndDate(date);
    const onStartDateChange = (date: Date | null) => setStartDate(date);

    const handleAlertsPerson = async () => {
        if (currentUserId != null) setSelectedUserModal(currentUserId);
    };

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checkboxId = Number(event.target.value);
        const isChecked = event.target.checked;

        const allEnumValues = Object.values(AlertStatusEnum)
            .filter((value) => typeof value === 'number') as number[];

        if (checkboxId === 0) {
            setSelectedOptions(isChecked ? [...allEnumValues, 0] : []);
        } else {
            setSelectedOptions(prev => {
                if (isChecked) {
                    const next = [...prev, checkboxId];
                    const selectedEnums = next.filter(o => o !== 0);
                    return selectedEnums.length === allEnumValues.length ? [...next, 0] : next;
                }
                return prev.filter(o => o !== checkboxId && o !== 0);
            });
        }
    };

    const handleSaveModal = async () => {
        try {
            const body: ExportCSVData = {
                states: selectedOptions,
                userId: selectedUserModal,
                dateStart: startDate,
                dateEnd: endDate
            };
            const result = await saveAlertsCsv(body);
            closeModal();
            showAlert({
                message: t("alert:modalDownload.modalImportAlertSucces", { records: result.recordCount, filename: result.filename }),
                type: 'success',
                duration: 8000
            });
        } catch (error) {
            showAlert({ message: t("alert.AlertErrorUploadCSV"), type: 'danger', duration: 8000 });
            console.log(error);
        }
    };

    return (
        <Container fluid className="mt-4" style={{ overflowX: 'auto' }}>
            <ModalGlobal
                show={showModal}
                onClose={closeModal}
                title={t("alert:modalDownload.modalImportTitle")}
                onSave={handleSaveModal}
                body={
                    <div>
                        <Form>
                            <Form.Check
                                className="my-4"
                                type="switch"
                                id="custom-switch2"
                                label={t("common:buttons.toggleAssignUser")}
                                onChange={handleAlertsPerson}
                            />
                        </Form>
                        <label>{t("alert:modalDownload.modalImportSubtitle")}</label>
                        <div>
                            {Object.entries(AlertStatusEnum)
                                .filter(([key]) => isNaN(Number(key)))
                                .map(([name, value]) => (
                                    <div key={value} style={{ marginBottom: '10px' }}>
                                        <input
                                            type="checkbox"
                                            id={`answer-type-${value}`}
                                            value={value}
                                            onChange={handleCheckboxChange}
                                            checked={selectedOptions?.includes(Number(value)) || false}
                                        />
                                        <label htmlFor={`answer-type-${value}`} style={{ marginLeft: '8px' }}>
                                            {t(`alert:states.${name}`)}
                                        </label>
                                    </div>
                                ))}
                            <div style={{ marginBottom: '10px' }}>
                                <input
                                    type="checkbox"
                                    id="allstate"
                                    value="0"
                                    checked={selectedOptions?.includes(0)}
                                    onChange={handleCheckboxChange}
                                />
                                <label htmlFor="allstate" style={{ marginLeft: '8px' }}>
                                    {t("alert:states.allStates")}
                                </label>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <div style={{ flex: 1 }}>
                                <InputDatePicker
                                    value={startDate}
                                    onChange={onStartDateChange}
                                    label={t("alert:modalDownload.modalImportAlertDateStart")}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <InputDatePicker
                                    value={endDate}
                                    onChange={onEndDateChange}
                                    label={t("alert:modalDownload.modalImportAlertDateEnd")}
                                />
                            </div>
                        </div>
                        {error && <small className="text-danger">{error}</small>}
                    </div>
                }
                saveText={t("common:buttons.buttonExport")}
                closeText={t("common:buttons.buttonCancel")}
                isLoading={false}
            />

            <Row>
                <Col xs={12}>
                    {isVisible && (
                        <AlertMessage
                            message={alert!.message}
                            type={alert!.type}
                            onClose={hideAlert}
                        />
                    )}
                </Col>
            </Row>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                <div style={{ flex: 1 }}>
                    <Search
                        searchText={searchText}
                        onChange={handleSearchChange}
                        onSearch={handleSearch}
                        onClear={handleClear}
                    />
                </div>
                <Button variant="" onClick={handleFilterToggle} aria-controls="collapse-filter-id" style={{ border: 0 }}>
                    <img src="/filter.png" alt="Filtrar" style={{ width: '50px' }} />
                </Button>
            </div>

            {filters.length > 0 && (
                <FiltersContainer
                    filters={filters}
                    isFilterVisible={isFilterVisible}
                    tableKey='alerts'
                    historicalKey='alerts'
                    onFiltersChange={handleFiltersChange}
                />
            )}

            <div className="d-flex align-items-center justify-content-between">
                <Form>
                    <Form.Check
                        className="my-4"
                        type="switch"
                        id="custom-switch"
                        label={t("common:buttons.toggleAssignUser")}
                        checked={filterByCurrentUser}
                        onChange={handleToggle}
                    />
                </Form>
                <Button
                    variant=""
                    onClick={(e) => { e.preventDefault(); openAlertModal(); }}
                    style={{ border: 0 }}
                >
                    <img src="/download.png" alt="ButtonExportTitle" style={{ width: '50px' }} />
                </Button>
            </div>

            <DragDropContext onDragEnd={handleDragDrop}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                    {columnsData.map((column) => (
                        <Droppable key={column.id} droppableId={column.id}>
                            {(provided, snapshot) => (
                                <div ref={provided.innerRef} {...provided.droppableProps}>
                                    <DroppableColumn
                                        isDraggingOver={snapshot.isDraggingOver}
                                        title={t(`alert:${column.name}`)}
                                    >
                                        {column.items.map((item, index) => (
                                            <Draggable
                                                key={item.id}
                                                draggableId={item.id.toString()}
                                                index={index}
                                            >
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                    >
                                                        <DraggableCard
                                                            alertCard={item}
                                                            isDragging={snapshot.isDragging}
                                                            onSurveyClick={onSurveyClick}
                                                            onClaimClick={onClaimClick}
                                                        />
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                    </DroppableColumn>
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    ))}
                </div>
            </DragDropContext>
        </Container>
    );
}